import React from 'react';
import PropTypes from 'prop-types';
import { Box, Paper, Typography } from '@mui/material';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { PipelineRunStatus } from 'redux/slices/pipelines/constants/pipelineRunStatus';
import RunErrorText from './RunErrorText';

// deploy-pipelines-run-cancel (Wave 5, AC-25/OQ-D) — the run console's cancellation notice.
//
// The BE reuses `PipelineRun.ErrorMessage` to carry a cancelled run's reason (no new column, which would
// have meant a third DDL script). So the SAME field feeds two very different presentations, and the reason
// a distinct sixth status was chosen at all is defeated if a deliberate cancel is rendered under the
// failure-reason treatment Wave 1 built for `Failed`. Hence: neutral `text.secondary`/`divider` (never
// `error.main`), a neutral icon, and the label "Run cancelled" — not "Run failed".
//
// EXTRACTED from PipelineDetail (independent code review, M3). It previously lived inline there, which made
// it effectively untestable — PipelineDetail pulls in 35 imports, redux, the router and ten data hooks, so
// any test of it would have been mostly mock scaffolding. Worse, the AC-25 test that DID exist rendered
// `PipelineRunStatusView`'s cancelled Alert, which the console never shows (PipelineDetail passes
// `hideDetails`, so that component returns early) — the AC was ticked against a surface the user never sees.
// As a standalone presentational component this renders in three lines of test setup and is pinned properly.
const CancelledRunNotice = ({ run }) => {
	if (run?.Status !== PipelineRunStatus.Cancelled || !run?.ErrorMessage) {
		return null;
	}

	return (
		<Paper
			variant="outlined"
			square
			sx={{ mx: 4, mt: 3, p: 2.5, bgcolor: 'background.default', borderColor: 'divider', borderLeftWidth: 4 }}
		>
			<Box display="flex" alignItems="flex-start" gap={1.5}>
				<CancelOutlinedIcon fontSize="small" sx={{ color: 'text.secondary', mt: 0.25 }} aria-hidden="true" />
				<Box flex={1} minWidth={0}>
					<Typography variant="subtitle2" color="text.secondary">
						Run cancelled
					</Typography>
					<RunErrorText message={run.ErrorMessage} maxHeight={160} />
				</Box>
			</Box>
		</Paper>
	);
};

CancelledRunNotice.propTypes = {
	// The currently displayed run. Renders nothing unless it is Cancelled AND carries a reason.
	run: PropTypes.shape({
		Status: PropTypes.number,
		ErrorMessage: PropTypes.string
	})
};

export default CancelledRunNotice;
